// SPDX-License-Identifier: AGPL-3.0-or-later

// LOCAL-ONLY: the favorites preview's data (favorites-bookmarks-plan.md sections 1.2, 1.3 and 3): the last message of
// each favorited channel, keyed by channel id, whether or not the channel is loaded in the message store (which is
// left as it is: it still drops messages for channels that aren't open).
// - Loads with POST /channels/messages/bulk, 25 channels a request, one request at a time, at most 10 per 10 s (half
//   the endpoint's bucket, which the unread popout shares); at READY, RESUMED, and for a favorite added later.
// - MESSAGE_CREATE / UPDATE / DELETE keep it live. The previewed message deleted (or a relocation's notice, a bulk
//   delete with no ids) fetches that channel again, collected for 250 ms.
// - A live event that arrives while its channel is loading wins over the load.
// undefined: not loaded; null: no message, or no access. Exclude from upstream sync.

import {Endpoints} from '@app/features/app/constants/Endpoints';
import {registerReconnectHandler} from '@app/features/gateway/events/ReconnectHandlers';
import {Message} from '@app/features/messaging/models/MessagingMessage';
import Favorites from '@app/features/messaging/state/Favorites';
import {http} from '@app/features/platform/transport/RestTransport';
import {Logger} from '@app/features/platform/utils/AppLogger';
import type {Message as WireMessage} from '@fluxer/schema/src/domains/message/MessageResponseSchemas';
import {observable, reaction, runInAction} from 'mobx';

export const PREVIEW_BATCH_SIZE = 25;
export const PREVIEW_REQUESTS_PER_WINDOW = 10;
export const PREVIEW_WINDOW_MS = 10_000;
export const PREVIEW_REFETCH_DELAY_MS = 250;

const logger = new Logger('FavoritePreviews');

interface BulkPreviewResponse {
	channels?: Array<{channel_id: string; messages: Array<WireMessage>}>;
}

function isNewer(id: string, than: string): boolean {
	return BigInt(id) > BigInt(than);
}

class FavoritePreviews {
	private readonly previews = observable.map<string, Message | null>();
	private favoriteIds = new Set<string>();
	private readonly queue = new Map<string, number>();
	/** Per channel, bumped by each live event; a load whose snapshot is older doesn't overwrite it. */
	private readonly liveVersion = new Map<string, number>();
	private sentAt: Array<number> = [];
	private sending = false;
	private pumpScheduled = false;
	private pumpTimer: ReturnType<typeof setTimeout> | null = null;
	private readonly refetch = new Set<string>();
	private refetchTimer: ReturnType<typeof setTimeout> | null = null;
	private stopWatching: (() => void) | null = null;
	private generation = 0;

	get(channelId: string): Message | null | undefined {
		return this.previews.get(channelId);
	}

	handleReady(): void {
		this.watchFavorites();
		this.loadAll();
	}

	handleReconnect(): void {
		this.loadAll();
	}

	handleMessageCreate(data: WireMessage): void {
		const channelId = data.channel_id;
		if (!this.favoriteIds.has(channelId)) return;
		const current = this.previews.get(channelId);
		if (current && !isNewer(data.id, current.id)) return;
		this.setLive(channelId, new Message(data, {missingReactions: 'preserve'}));
	}

	handleMessageUpdate(data: Partial<WireMessage> & {id: string}): void {
		const channelId = data.channel_id ?? this.channelPreviewing(data.id);
		if (!channelId || !this.favoriteIds.has(channelId)) return;
		const current = this.previews.get(channelId);
		if (!current || current.id !== data.id) return;
		this.setLive(channelId, current.withUpdates(data));
	}

	handleMessageDelete(channelId: string, messageId: string): void {
		if (!this.favoriteIds.has(channelId) || this.previews.get(channelId)?.id !== messageId) return;
		this.setLive(channelId, null);
		this.scheduleRefetch(channelId);
	}

	handleMessageDeleteBulk(channelId: string, messageIds: ReadonlyArray<string>): void {
		if (!this.favoriteIds.has(channelId)) return;
		const current = this.previews.get(channelId);
		const previewed = current != null && messageIds.includes(current.id);
		if (previewed) this.setLive(channelId, null);
		if (previewed || messageIds.length === 0) this.scheduleRefetch(channelId);
	}

	reset(): void {
		this.stopWatching?.();
		this.stopWatching = null;
		if (this.pumpTimer) clearTimeout(this.pumpTimer);
		if (this.refetchTimer) clearTimeout(this.refetchTimer);
		this.pumpTimer = null;
		this.refetchTimer = null;
		this.generation++;
		this.favoriteIds = new Set();
		this.queue.clear();
		this.liveVersion.clear();
		this.refetch.clear();
		this.sentAt = [];
		this.sending = false;
		this.pumpScheduled = false;
		runInAction(() => this.previews.clear());
	}

	private currentFavoriteIds(): Array<string> {
		return Favorites.channels.map((favorite) => favorite.channelId);
	}

	private watchFavorites(): void {
		if (this.stopWatching) return;
		this.stopWatching = reaction(
			() => this.currentFavoriteIds(),
			(ids) => this.syncFavorites(ids),
			{equals: (a, b) => a.length === b.length && a.every((id, index) => id === b[index])},
		);
	}

	private syncFavorites(ids: ReadonlyArray<string>): void {
		const next = new Set(ids);
		for (const id of this.favoriteIds) {
			if (next.has(id)) continue;
			this.queue.delete(id);
			this.refetch.delete(id);
			this.liveVersion.delete(id);
			runInAction(() => this.previews.delete(id));
		}
		const added = ids.filter((id) => !this.favoriteIds.has(id));
		this.favoriteIds = next;
		for (const id of added) this.enqueue(id);
	}

	private loadAll(): void {
		this.favoriteIds = new Set(this.currentFavoriteIds());
		for (const id of this.favoriteIds) this.enqueue(id);
	}

	private channelPreviewing(messageId: string): string | undefined {
		for (const [channelId, preview] of this.previews) {
			if (preview?.id === messageId) return channelId;
		}
		return undefined;
	}

	private setLive(channelId: string, preview: Message | null): void {
		this.liveVersion.set(channelId, (this.liveVersion.get(channelId) ?? 0) + 1);
		runInAction(() => this.previews.set(channelId, preview));
	}

	private scheduleRefetch(channelId: string): void {
		this.refetch.add(channelId);
		if (this.refetchTimer) return;
		this.refetchTimer = setTimeout(() => {
			this.refetchTimer = null;
			for (const id of this.refetch) this.enqueue(id);
			this.refetch.clear();
		}, PREVIEW_REFETCH_DELAY_MS);
	}

	/** Queued in the same turn (a READY's whole list, a burst of deletes) go out together. */
	private enqueue(channelId: string): void {
		this.queue.delete(channelId);
		this.queue.set(channelId, this.liveVersion.get(channelId) ?? 0);
		if (this.pumpScheduled) return;
		this.pumpScheduled = true;
		const generation = this.generation;
		void Promise.resolve().then(() => {
			if (generation !== this.generation) return;
			this.pumpScheduled = false;
			this.pump();
		});
	}

	private pump(): void {
		if (this.sending || this.pumpTimer || this.queue.size === 0) return;
		const now = Date.now();
		this.sentAt = this.sentAt.filter((at) => now - at < PREVIEW_WINDOW_MS);
		if (this.sentAt.length >= PREVIEW_REQUESTS_PER_WINDOW) {
			this.pumpTimer = setTimeout(
				() => {
					this.pumpTimer = null;
					this.pump();
				},
				this.sentAt[0]! + PREVIEW_WINDOW_MS - now,
			);
			return;
		}
		const batch = Array.from(this.queue).slice(0, PREVIEW_BATCH_SIZE);
		for (const [channelId] of batch) this.queue.delete(channelId);
		this.sentAt.push(now);
		this.sending = true;
		const generation = this.generation;
		void this.load(batch).finally(() => {
			if (generation !== this.generation) return;
			this.sending = false;
			this.pump();
		});
	}

	private async load(batch: ReadonlyArray<[string, number]>): Promise<void> {
		const generation = this.generation;
		let response: BulkPreviewResponse | undefined;
		try {
			response = (
				await http.post<BulkPreviewResponse>(Endpoints.CHANNEL_MESSAGES_BULK, {
					body: {requests: batch.map(([channelId]) => ({channel_id: channelId, limit: 1}))},
				})
			).body;
		} catch (error) {
			logger.error('Loading favorite previews failed:', error);
			return;
		}
		if (generation !== this.generation) return;
		const byChannel = new Map((response?.channels ?? []).map((entry) => [entry.channel_id, entry.messages]));
		runInAction(() => {
			for (const [channelId, version] of batch) {
				if (!this.favoriteIds.has(channelId)) continue;
				if ((this.liveVersion.get(channelId) ?? 0) !== version) continue;
				const latest = byChannel.get(channelId)?.[0];
				this.previews.set(channelId, latest ? new Message(latest, {missingReactions: 'preserve'}) : null);
			}
		});
	}
}

const favoritePreviews = new FavoritePreviews();
// RESUMED: every favorite again, through the fork's reconnect list (section 10).
registerReconnectHandler(() => favoritePreviews.handleReconnect());
export default favoritePreviews;
